// ⌘K / Ctrl+K «Cambiar de conversación» — the Mac's QuickSwitcherSheet (ios/Sources/Views/
// QuickSwitcherSheet.swift; Windows QuickSwitcherViewModel). Candidates are the CACHED sidebar the
// workspace already built (every category's conversations of this team, DMs and groups included):
// opening and filtering make no worker read and no request. Most recently active first; accent- and
// case-folded matching on the shown name or the channel slug (core/quickSwitcherPresentation.ts). A
// KEYBOARD tool: ↑/↓ move the highlight, Enter opens it, Escape closes.
import { useEffect, useMemo, useRef, useState } from 'react';
import { QuickSwitcherPresentation, type QuickSwitcherRow } from '../../../core/quickSwitcherPresentation';
import type { ChannelRecord } from '../../../core/records';
import { Icon, type IconName } from '../../../design/icons';
import { t } from '../../../i18n/i18n';
import { Dialog, EmptyState, SearchInput } from '../../kit/Kit';
import type { SidebarModel } from '../../models/sidebarModel';
import { ConversationFace } from '../../sidebar/Sidebar';
import '../people/people.css';
import './nav.css';

function icon(row: QuickSwitcherRow): IconName {
  switch (row.type) {
    case 'P':
      return 'lock';
    case 'D':
      return 'person';
    case 'G':
      return 'people';
    default:
      return 'hash';
  }
}

/** The switcher's rows from the sidebar model: every row the sections hold, named as the list names it. */
export function switcherRows(model: SidebarModel): QuickSwitcherRow[] {
  const names = new Map<string, string>();
  const channels: ChannelRecord[] = [];
  for (const section of model.sections) {
    for (const row of section.rows) {
      if (names.has(row.id)) continue;
      const channel = model.channelsById.get(row.id);
      if (!channel) continue;
      names.set(row.id, row.name);
      // The row's own newest activity (the channel record can lag a socket message) — the value the
      // sidebar sorts by, so the switcher's "recent first" agrees with the list.
      channels.push({ ...channel, lastPostAt: row.lastPostAt });
    }
  }
  return QuickSwitcherPresentation.rows(channels, names);
}

export function QuickSwitcher({ model, currentChannelId, onOpen, onClose }: { model: SidebarModel; currentChannelId: string | null; onOpen(channelId: string): void; onClose(): void }) {
  // Built ONCE per open: a message arriving while the switcher is up must not reorder it under the keys.
  const all = useMemo(() => switcherRows(model), []); // eslint-disable-line react-hooks/exhaustive-deps
  const [query, setQuery] = useState('');
  const [highlighted, setHighlighted] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);
  const visible = useMemo(() => QuickSwitcherPresentation.filtered(all, query), [all, query]);
  // DMs and groups lead with their FACES, exactly like the sidebar row (user report 2026-10-27:
  // «it is expected to show the avatar instead of the icons»); channels keep the glyph.
  const sidebarRows = useMemo(() => new Map(model.sections.flatMap((s) => s.rows).map((r) => [r.id, r])), []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => setHighlighted(0), [query]);
  useEffect(() => {
    listRef.current?.querySelector(`[data-index="${highlighted}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [highlighted]);

  const open = (row: QuickSwitcherRow) => {
    onClose();
    if (row.channelId !== currentChannelId) onOpen(row.channelId);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      if (!visible.length) return;
      e.preventDefault();
      setHighlighted((h) => Math.max(0, Math.min(visible.length - 1, h + (e.key === 'ArrowDown' ? 1 : -1))));
    } else if (e.key === 'Enter') {
      const row = visible[highlighted];
      if (row) {
        e.preventDefault();
        open(row);
      }
    }
  };

  return (
    <Dialog title={t('switcher.title')} onClose={onClose} width={520}>
      <div className="people-dialog-root nav-switcher" onKeyDown={onKeyDown}>
        <div className="people-sticky">
          <SearchInput value={query} onChange={setQuery} placeholder={t('switcher.placeholder')} autoFocus />
        </div>
        {visible.length === 0 ? <EmptyState title={all.length === 0 ? t('switcher.empty') : t('switcher.noMatches')} /> : null}
        <div className="people-list" ref={listRef} role="listbox" aria-label={t('switcher.title')}>
          {visible.map((row, index) => (
            <button
              type="button"
              key={row.channelId}
              data-index={index}
              data-channel-id={row.channelId}
              role="option"
              aria-selected={index === highlighted}
              className={`list-row clickable nav-switcher-row${index === highlighted ? ' highlighted' : ''}`}
              onMouseMove={() => index !== highlighted && setHighlighted(index)}
              onClick={() => open(row)}
              tabIndex={-1}
            >
              {(row.type === 'D' || row.type === 'G') && sidebarRows.get(row.channelId) ? (
                <span className="list-row-leading nav-switcher-face">
                  <ConversationFace row={sidebarRows.get(row.channelId)!} />
                </span>
              ) : (
                <span className="list-row-leading nav-switcher-icon">
                  <Icon name={icon(row)} size={15} />
                </span>
              )}
              <span className="list-row-text">
                <span className="list-row-title">{row.name}</span>
              </span>
              {row.channelId === currentChannelId ? <span className="list-row-trailing nav-muted">{t('switcher.current')}</span> : null}
            </button>
          ))}
        </div>
      </div>
    </Dialog>
  );
}
